import React from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon, CheckIcon } from '@chakra-ui/icons';

interface StepNavigationProps {
  onNext: () => void;
  onPrevious: () => void;
  onSkip?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  nextLabel?: string;
  previousLabel?: string;
  skipLabel?: string;
  isNextDisabled?: boolean;
  isPreviousDisabled?: boolean;
  showSkip?: boolean;
  isLoading?: boolean;
}

const StepNavigation: React.FC<StepNavigationProps> = ({
  onNext,
  onPrevious,
  onSkip,
  isFirstStep = false,
  isLastStep = false,
  nextLabel = 'Next',
  previousLabel = 'Back',
  skipLabel = 'Skip',
  isNextDisabled = false,
  isPreviousDisabled = false,
  showSkip = false,
  isLoading = false,
}) => {
  const blueBg = useColorModeValue('blue.500', 'blue.400');
  const blueHoverBg = useColorModeValue('blue.600', 'blue.500');
  const greenBg = useColorModeValue('green.500', 'green.400');
  const greenHoverBg = useColorModeValue('green.600', 'green.500');
  
  const buttonBg = isLastStep ? greenBg : blueBg;
  const buttonHoverBg = isLastStep ? greenHoverBg : blueHoverBg;
  const secondaryBg = useColorModeValue('gray.100', 'gray.700');
  const secondaryHoverBg = useColorModeValue('gray.200', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const skipColor = useColorModeValue('gray.500', 'gray.400');

  const finalNextLabel = isLastStep ? 'Complete' : nextLabel;

  return (
    <>
      {/* Left side - Back button */}
      {!isFirstStep && (
        <Box
          position="fixed"
          bottom="50%"
          transform="translateY(50%)"
          left={{ base: 4, md: 8 }}
          zIndex={10}
        >
          <IconButton
            aria-label="Previous step"
            icon={<ArrowBackIcon boxSize={6} />}
            onClick={onPrevious}
            isDisabled={isPreviousDisabled || isLoading}
            bg={secondaryBg}
            _hover={{ bg: secondaryHoverBg, transform: 'scale(1.1)' }}
            size="lg"
            borderRadius="full"
            boxSize="60px"
            shadow="lg"
            transition="all 0.2s"
          />
        </Box>
      )}

      {/* Center - Skip button (if applicable) */}
      {showSkip && onSkip && (
        <Box
          position="fixed"
          bottom={8}
          left="50%"
          transform="translateX(-50%)"
          zIndex={10}
        >
          <Button
            onClick={onSkip}
            variant="ghost"
            size="md"
            color={skipColor}
            _hover={{ textDecoration: 'underline' }}
            isDisabled={isLoading}
          >
            {skipLabel}
          </Button>
        </Box>
      )}

      {/* Right side - Next/Complete button */}
      <Box
        position="fixed"
        bottom="50%"
        transform="translateY(50%)"
        right={{ base: 4, md: 8 }}
        zIndex={10}
      >
        <IconButton
          aria-label={isLastStep ? "Complete" : "Next step"}
          icon={isLastStep ? <CheckIcon boxSize={6} /> : <ArrowForwardIcon boxSize={6} />}
          onClick={onNext}
          isDisabled={isNextDisabled || isLoading}
          isLoading={isLoading}
          bg={buttonBg}
          color="white"
          _hover={{ bg: buttonHoverBg, transform: 'scale(1.1)' }}
          size="lg"
          borderRadius="full"
          boxSize="60px"
          shadow="xl"
          transition="all 0.2s"
        />
      </Box>
    </>
  );
};

export default StepNavigation;